import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardFooter, Navbar, Nav, NavItem } from 'reactstrap';
import { Button, CardHeader, PseudoStates, Table, colors } from '@cordata/buffet';
import { Link, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { camelCase, startCase } from 'lodash';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  authorizedInstance as axios,
  useQueryParam,
} from '~/utils';

const ChildQueueNavItem = styled(NavItem)`
  background-color: ${p => (p.active ? colors[7] : '')};
  text-align: center;
  color: ${p => (p.active ? 'white' : 'black')};
  cursor: pointer;
  display: flex;
  max-height: 100px;

  &:before {
    content: "";
    width: 0;
    height: 0;
    border-top: 50px solid ${p => (p.active ? colors[7] : 'white')};
    border-bottom: 50px solid ${p => (p.active ? colors[7] : 'white')};
    border-left: 30px solid white;
  }

  &:after {
    content: "";
    width: 0;
    height: 0;
    border-top: 50px solid white;
    border-bottom: 50px solid white;
    border-left: 30px solid ${p => (p.active ? colors[7] : 'white')};
  }

  &:hover {
    background-color: ${p => (p.active ? colors[7] : 'lightgray')};

    &:before {
      border-top: 50px solid ${p => (p.active ? colors[7] : 'lightgray')};
      border-bottom: 50px solid ${p => (p.active ? colors[7] : 'lightgray')};
    }

    &:after {
      border-left: 30px solid ${p => (p.active ? colors[7] : 'lightgray')};
    }
  }

  &:first-child {
    &:before {
      border-left: 30px solid ${p => (p.active ? colors[7] : 'white')};
    }
  }

  &:last-child {
    &:after {
      border-top: 50px solid ${p => (p.active ? colors[7] : 'white')};
      border-bottom: 50px solid ${p => (p.active ? colors[7] : 'white')};
    }
  }

  &:first-child:hover {
    &:before {
      border-left: 30px solid ${p => (p.active ? colors[7] : 'lightgray')};
    }
  }

  &:last-child:hover {
    &:after {
      border-top: 50px solid ${p => (p.active ? colors[7] : 'lightgray')};
      border-bottom: 50px solid ${p => (p.active ? colors[7] : 'lightgray')};
    }
  }
`;

const HomeQueueCard = ({ parentQueue }) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState();
  const [selectedChild, setSelectedChild] = useState();
  const [queueData, setQueueData] = useState([]);

  useEffect(() => {
    if (selectedChild) {
      setError(undefined);
      setIsLoading(true);

      axios
        .get(`queues/${selectedChild.id}`)
        .then(q => q.data.data)
        .then(setQueueData)
        .catch(setError)
        .then(() => setIsLoading(false));
    }
  }, [selectedChild]);

  const [selectedQueueId, setSelectedQueueId] = useQueryParam('q');

  const [sort, setSort] = useQueryParam('sort', [], {
    serialize: (newSorted) => newSorted.map(s => `${s.desc ? '-' : ''}${s.id}`).join(','),
    deserialize: (string) => string.split(',').map(s => ({ desc: s.startsWith('-'), id: s.replace('-', '') })),
  });

  useEffect(() => {
    if (parentQueue.childQueues && parentQueue.childQueues.length) {
      if (selectedQueueId) {
        setSelectedChild(parentQueue.childQueues.find(q => q.id === selectedQueueId));
        setSort([]);
      } else {
        setSelectedQueueId(parentQueue.childQueues[0].id);
      }
    }
  }, [selectedQueueId]);

  useEffect(() => {
    if (selectedChild) {
      const localStorageSort = localStorage.getItem(`sort-${selectedChild.id}`);

      if (localStorageSort !== null) {
        setSort(JSON.parse(localStorageSort));
      }
    }
  }, [selectedChild]);

  useEffect(() => {
    if (selectedChild) {
      localStorage.setItem(`sort-${selectedChild.id}`, JSON.stringify(sort));
    }
  }, [sort]);

  return (
    <Card>
      <CardHeader
        className="text-light"
        color="primary"
        action={
          <Button color="light" outline size="sm" className="cursor-default font-weight-bold">{parentQueue.attributes.count}</Button>
        }
      >
        {parentQueue.attributes.queueLabel}
      </CardHeader>

      <CardBody>
        {parentQueue.childQueues && (
          <Navbar expand="md" className="mb-3">
            <Nav navbar className="w-100">
              {parentQueue.childQueues.map(q => (
                <ChildQueueNavItem
                  key={q.id}
                  active={q.id === (selectedChild || {}).id}
                  onClick={() => setSelectedQueueId(q.id)}
                  className="p-0"
                >
                  <div className="d-block m-2">
                    <h1>{q.attributes.count}</h1>

                    <h5>{q.attributes.queueLabel}</h5>
                  </div>
                </ChildQueueNavItem>
              ))}
            </Nav>
          </Navbar>
        )}

        {!isLoading && !error ? (
          <Table
            defaultSorted={sort}
            onSortedChange={setSort}
            columns={queueData.attributes.queueListViewColumnMap.map(h => ({
              accessor: camelCase(h.columnName),
              Header: startCase(h.displayHeader),
              show: !h.isHidden,
            }))}
            defaultPageSize={5}
            data={queueData.attributes.data}
            getTrProps={(state, rowInfo) => ({
              style: {
                cursor: 'pointer',
              },
              onClick: (e, handleOriginal) => (handleOriginal ? handleOriginal() : history.push(`/patients/${rowInfo.original.patientUid}/details`, rowInfo.original)),
            })}
          />
        ) : (
          <PseudoStates isLoading={isLoading} error={error} />
        )}
      </CardBody>

      <CardFooter className="text-center" tag={Link} to={`/home/queues/${(selectedChild || {}).id}`}>
        {selectedChild && (
          <>
            See All {selectedChild.attributes.count} {selectedChild.attributes.queueLabel} <FontAwesomeIcon icon="chevron-right" size="sm" />
          </>
        )}
      </CardFooter>
    </Card>
  );
};

HomeQueueCard.propTypes = {
  parentQueue: PropTypes.shape({
    childQueues: PropTypes.arrayOf(PropTypes.object.isRequired),
    attributes: PropTypes.shape({
      count: PropTypes.number.isRequired,
      queueLabel: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default HomeQueueCard;
